<template>
    <div
        class="DragAndDropMemo"
        @mousemove="drag"
        @mouseup="dragEnd"
        @mouseleave="dragEnd"
        @touchmove="drag"
        @touchend="dragEnd"
    >
        <h3>{{ $t('instruction') }}</h3>
        <h3 class="number">{{ number }}</h3>
        <div class="drop-boxes">
            <div v-for="type of imagesTypes" :key="type" class="drop-boxes-item">
                <h5>{{ type }}</h5>
                <div
                    class="drop-box"
                    :number="number"
                    :type="type"
                    @mouseenter="iluminateTile"
                    @mouseleave="resetIluminationFromTile"
                >
                </div>
            </div>
        </div>

        <div class="select-area">
            <div
                v-for="(item, i) of selectAreaItems"
                :key="i"
                class="select-image-wrapper"
                @mousedown="dragStart($event, item)"
                @touchstart="dragStart($event, item)"
            >
                <img
                    v-if="item.imageType !== 'object'"
                    :src="require(`@/assets/images/system-images/olpa-system/${item.imageType}/${item.number}.jpg`)"
                    alt="select-item-image"
                    draggable="false"
                />

                <AnimatedNumbershape v-else class="numbershape" :number="item.number" reversed />
            </div>
        </div>
    </div>
</template>

<translations>
  instruction: 'Put items in the right place'
  instruction_no: 'Plasser i riktig boks'
</translations>

<script>
import AnimatedNumbershape from '@/components/AnimatedNumbershape';

export default {
    components: {
        AnimatedNumbershape
    },

    props: {
        number: {
            type: String,
            required: true
        },

        stepItems: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            imagesTypes: ['object', 'person', 'action'],
            randomizedSelectItems: [],
            selectedItem: null,
            itemUnderMatchNumber: null,
            itemUnderMatchType: null,
            itemsMatchedCount: 0,
            dragAndDrop: {
                dragItem: null,
                currentX: null,
                currentY: null,
                initialX: null,
                initialY: null,
                active: false
            }
        };
    },

    computed: {
        stepItemsNumbers() {
            return this.stepItems.map(item => item.number);
        },

        selectAreaItems: {
            get: function () {
                return this.randomizedSelectItems;
            },

            set: function (newArr) {
                this.randomizedSelectItems = newArr;
            }
        }
    },

    mounted() {
        this.setRandomizedSelectItems();
    },

    methods: {
        setRandomizedSelectItems() {
            const imagesNumbersByType = [];

            this.imagesTypes.forEach(imageType => {
                this.stepItemsNumbers.forEach(number => {
                    imagesNumbersByType.push({
                        imageType,
                        number
                    });
                });
            });

            this.randomizedSelectItems = this.shuffleItemsInArray(imagesNumbersByType);
        },

        shuffleItemsInArray(array) {
            return [...array.sort(() => 0.5 - Math.random())];
        },

        dragStart(e, selectedItem) {
            const dragItem = e.path.find(domElement => domElement.className === 'select-image-wrapper');

            this.selectedItem = selectedItem;

            if (e.type === 'touchstart') {
                this.dragAndDrop.initialX = e.touches[0].clientX;
                this.dragAndDrop.initialY = e.touches[0].clientY;
            } else {
                this.dragAndDrop.initialX = e.clientX;
                this.dragAndDrop.initialY = e.clientY;
            }

            this.dragAndDrop.active = true;
            this.dragAndDrop.dragItem = dragItem;

            this.setDraggedItemZIndex(10);
            this.setPointerEvent(false);
        },

        setDraggedItemZIndex(zIndex) {
            const { dragItem } = this.dragAndDrop;

            dragItem.style.zIndex = zIndex;
        },

        setPointerEvent(flag) {
            const { dragItem } = this.dragAndDrop;

            dragItem.style.pointerEvents = flag ? 'auto' : 'none';
        },

        drag(e) {
            const { active, currentX, currentY, initialX, initialY } = this.dragAndDrop;

            if (active) {
                if (e.type === 'touchmove') {
                    this.dragAndDrop.currentX = e.touches[0].clientX - initialX;
                    this.dragAndDrop.currentY = e.touches[0].clientY - initialY;
                } else {
                    this.dragAndDrop.currentX = e.clientX - initialX;
                    this.dragAndDrop.currentY = e.clientY - initialY;
                }

                this.setDraggedItemPosition({ x: currentX, y: currentY });
            }
        },

        setDraggedItemPosition(cords) {
            const { dragItem } = this.dragAndDrop;
            const { x, y } = cords;

            dragItem.style.transform = `translate3d(${x}px,${y}px,0)`;
        },

        dragEnd(e) {
            const { dragItem } = this.dragAndDrop;
            const isItemAlreadyAddedToMatchArea = dragItem.parentElement.className === 'drop-box';

            if (dragItem && !isItemAlreadyAddedToMatchArea) {
                if (e.type === 'touchend') {
                    this.handleMobileDrop(e);
                } else {
                    this.handleDrop(e);
                }

                this.resetDragAndDrop();
            }

            this.dragAndDrop.active = false;
        },

        handleMobileDrop(e) {
            const matchedTile = document.elementFromPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
            const matchedTileNumber = matchedTile.attributes.number && matchedTile.attributes.number.value;
            const matchedTileType = matchedTile.attributes.type && matchedTile.attributes.type.value;

            const { imageType, number } = this.selectedItem;

            const areItemsMatching = matchedTileNumber === number && matchedTileType === imageType;

            if (areItemsMatching) {
                this.handleItemMatch();
            }
        },

        handleDrop(e) {
            const matchedTile = document.elementFromPoint(e.clientX, e.clientY);
            const matchedTileNumber = matchedTile.attributes.number && matchedTile.attributes.number.value;
            const matchedTileType = matchedTile.attributes.type && matchedTile.attributes.type.value;

            const { imageType, number } = this.selectedItem;

            const areItemsMatching = matchedTileNumber === number && matchedTileType === imageType;

            if (areItemsMatching) {
                this.handleItemMatch();
            }
        },

        resetDragAndDrop() {
            this.setDraggedItemZIndex(0);
            this.setDraggedItemPosition({ x: 0, y: 0 });
            this.resetDraggedItemCords();
            this.resetItemsMatchingInfo();
            this.setPointerEvent(true);
        },

        resetItemsMatchingInfo() {
            this.selectedItemNumber = null;
            this.itemUnderMatchNumber = null;
        },

        handleItemMatch() {
            this.removeMatchedItemFromSelectArea();
            this.addItemIntoMatchingArea();
            this.handleProgress();
        },

        removeMatchedItemFromSelectArea() {
            this.selectAreaItems = this.selectAreaItems.filter(
                selectAreaItem =>
                    selectAreaItem.imagesType !== this.itemUnderMatchNumber &&
                    selectAreaItem.number !== this.itemUnderMatchNumber
            );
        },

        addItemIntoMatchingArea() {
            const { dragItem } = this.dragAndDrop;

            const matchArea = document.querySelector('.drop-boxes');
            const matchedTile = matchArea.querySelector(`.drop-box[type="${this.selectedItem.imageType}"]`);

            matchedTile.style.border = 'none';

            matchedTile.appendChild(dragItem);

            this.animateMatchedItem(matchedTile);
        },

        animateMatchedItem(matchedTile) {
            matchedTile.style.transform = 'scale(1.2)';

            setTimeout(() => {
                matchedTile.style.transform = 'scale(1)';
            }, 300);
        },

        handleProgress() {
            this.itemsMatchedCount++;

            if (this.itemsMatchedCount === 3) {
                this.$emit('userFinishedMemo');
            }
        },

        resetDraggedItemCords() {
            this.dragAndDrop.currentX = null;
            this.dragAndDrop.currentY = null;
        },

        iluminateTile(e) {
            const { dragItem } = this.dragAndDrop;

            if (dragItem) {
                e.target.style.backgroundColor = 'hsla(0, 0%, 100%, 0.5)';
            }
        },

        resetIluminationFromTile(e) {
            e.target.style.backgroundColor = 'inherit';
        }
    }
};
</script>

<style lang="scss" scoped>
.DragAndDropMemo {
    h3 {
        text-align: center;
        font-size: 140%;
        font-weight: normal;
        margin-bottom: 0;
        &.number {
            font-size: 200%;
            color: rgba(white, 0.4);
        }
    }

    .drop-boxes {
        display: flex;
        .drop-boxes-item {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            h5 {
                color: rgba(white, 0.7);
                font-size: 18px;
                font-weight: normal;
                margin-bottom: 20px;
            }
            .drop-box {
                width: 90px;
                height: 90px;
                border: 1px dashed hsla(0, 0%, 100%, 0.5);
                border-radius: 10px;
                transition: 0.5s;
                img {
                    width: 100%;
                    height: 100%;
                    border-radius: 10px;
                }
                .numbershape {
                    background: white;
                    border-radius: 10px;
                    height: 90px;
                }
            }
        }
    }

    .select-area {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        background-color: hsla(0, 0%, 100%, 0.5);
        border-radius: 10px;
        margin-top: 40px;
        padding: 10px 0;
        .select-image-wrapper {
            width: 75px;
            height: 75px;
            margin: 10px;

            img {
                width: 100%;
                height: 100%;
                border-radius: 10px;
            }

            .numbershape {
                background: white;
                border-radius: 10px;
                height: 100%;
            }
        }
    }
}
</style>
